import { RankKit } from "@/components/site/RankKit";
import { BenchmarkPanel } from "@/components/rates/BenchmarkPanel";
import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { Section, SectionHeader, CTABand } from "@/components/site/Blocks";
import { buildHead } from "@/lib/seo-head";
import { getBenchmarkRates } from "@/lib/fred.functions";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import investorRatesHero from "@/assets/hero/hero-investor-rates-home-data.jpg";

const benchmarksOptions = queryOptions({
  queryKey: ["benchmarkRates"],
  queryFn: () => getBenchmarkRates(),
  staleTime: 60 * 60 * 1000, // 1h
  retry: 1,
});

export const Route = createFileRoute("/rates")({
  loader: ({ context }) => {
    context.queryClient.ensureQueryData(benchmarksOptions).catch(() => {});
  },
  head: () =>
    buildHead({
      title: "DSCR & Investor Loan Rates | Simply Approved Mortgages",
      description:
        "Live SOFR and 10-Yr Treasury benchmarks plus current DSCR, Foreign National, Construction, Fix & Flip, STR, and Section 8 rate bands.",
      url: "/rates",
      keywords:
        "DSCR loan rates today, investor loan rates, current DSCR interest rates, foreign national loan rates, fix and flip loan rates, construction loan rates, Airbnb loan rates, Section 8 loan rates, SOFR rate today, 10 year treasury rate, investor mortgage rate bands, business purpose loan rates",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Rates", url: "/rates" },
      ],
      service: { name: "Investor Loan Rate Bands" },
    }),
  component: Rates,
});

function BenchmarkStrip() {
  const { data } = useSuspenseQuery(benchmarksOptions);
  return <BenchmarkPanel data={data} showChange />;
}

function Rates() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="Rates"
        title="Investor loan rate bands"
        subtitle="Live wholesale benchmarks below, plus indicative pricing by program. Real rate locks require a completed application, credit review, appraisal, and property/entity docs — not an address alone."
        image={{
          src: investorRatesHero,
          alt: "Rental home with an abstract investor loan market data chart",
        }}
      />
      <RankKit page="rates" part="answer" />
      <LiveDscrRateSection
        strategyCards
        id="live-rates"
        eyebrow="Live pricing · DSCR"
        heading="Live DSCR pricing on a sample investor scenario"
        paragraphs={[
          "These options are actual pricing responses returned for a sample business-purpose DSCR scenario, each showing rate, APR, borrower points or lender credit, estimated principal and interest, and the lock period from the same quote. The indicative program bands further down update daily.",
        ]}
        links={[
          { to: "/investment-property-rates", label: "Price your own DSCR scenario" },
          { to: "/dscr-loan-rates", label: "DSCR pricing tiers by DSCR, LTV and FICO" },
        ]}
      />
      <Section>
        <BenchmarkStrip />
      </Section>
      <PageEssentials hideAnswer />
      <RankKit page="rates" part="details" />
      <Section>

        <SectionHeader eyebrow="Programs" title="How each investor program is structured" />

        <div className="mt-8 overflow-x-auto rounded-xl border border-border bg-card" tabIndex={0} aria-label="Investor loan program structures">
          <table className="min-w-[42rem] w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Program</th>
                <th className="px-4 py-3">Typical structure</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {[
                ["DSCR", "720+ FICO, ≥1.20 DSCR, 65% LTV"],
                ["Foreign National", "65–75% LTV (80% select), 6–12 mo reserves"],
                ["Construction", "IO, 12–24 mo, up to 85% LTC"],
                ["Fix & Flip", "IO, 12 mo, up to 90% purchase"],
                ["Airbnb / STR", "T-12 actuals or AirDNA projections"],
                ["Section 8", "HAP contract counted as verified income"],
                ["Bridge", "IO, 12–24 mo, 1–2 points, clear exit required"],
                ["Hard Money / Private", "IO, 1.5–3 points, up to 80% as-is value"],
              ].map((row) => (
                <tr key={row[0]}>
                  <td className="px-4 py-3 font-semibold text-brand">{row[0]}</td>
                  <td className="px-4 py-3 text-muted-foreground">{row[1]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs text-muted-foreground">Live DSCR pricing is shown in the rate center; other programs are priced per scenario. Actual pricing depends on FICO, LTV, DSCR, occupancy, reserves, prepayment structure, and market conditions. Not a commitment to lend.</p>
      </Section>



      <section data-inline-cta="">
        <div className="container mx-auto max-w-7xl px-4">
          <ProductReportCtaBanner productKey="dscr" variant="inline" />
        </div>
      </section>

      <CTABand title="Get real numbers on your deal" body="Initial scenario review on complete applications — usually same business day. Formal pricing and terms follow underwriting." />
    <PageOutro />
      </SiteShell>
  );
}
